/* eslint-disable @typescript-eslint/no-explicit-any */
import { WorkflowExecutionType } from '@common/Node/NodeSidebar/NodeExecutionHeader';
import { RTC } from '@components/Flow/Nodes/Trigger/InboundCall/Softphone/useRTC';
import { RequestError } from '@requests/common';
import { getWebhookEntity, WebhookEntityParams, WebhookEntityResponse } from '@requests/flow';
import { getMessagingProfiles, getVoiceApplications, TelnyxMessagingProfile, TelnyxVoiceApp } from '@requests/telnyx';
import { State } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

/**
 * Used to determine if the Execute Workflow button should be disabled.
 * @param type The workflow execution type.
 */

export const useEntityExecution = (type: WorkflowExecutionType, rtc?: RTC) => {
  const { service_id, workflow_id } = useParams();

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const trigger = commands.find((command) => command.config.trigger);

  const getProfileId = () => {
    if (trigger && type === WorkflowExecutionType.InboundCall) {
      const connection_id = trigger.state?.connection_id;
      return connection_id;
    }

    if (trigger && type === WorkflowExecutionType.InboundMessage) {
      const messaging_profile_id = trigger.state?.messaging_profile_id;
      return messaging_profile_id;
    }

    return undefined;
  };

  const profile_id = getProfileId();
  const voiceAppEnabled = type === WorkflowExecutionType.InboundCall;
  const messagingProfileEnabled = type === WorkflowExecutionType.InboundMessage;
  const entityRequestEnabled = Boolean(
    profile_id && (type === WorkflowExecutionType.InboundCall || type === WorkflowExecutionType.InboundMessage)
  );

  const {
    data: entity = null,
    isLoading: entityLoading,
    error: entityError,
  } = useQuery<WebhookEntityResponse, RequestError, WebhookEntityResponse, [string, WebhookEntityParams]>({
    queryKey: [
      'webhook_entity',
      { service_id, workflow_id, profile_id: profile_id as string, type: type as 'voice' | 'messaging' },
    ],
    queryFn: getWebhookEntity,
    enabled: entityRequestEnabled,
    retry: false,
  });

  const {
    data: applications = [],
    isLoading: appsLoading,
    error: voiceAppError,
  } = useQuery<TelnyxVoiceApp[], RequestError>({
    queryKey: ['voice_apps'],
    queryFn: getVoiceApplications,
    enabled: voiceAppEnabled,
    retry: false,
  });

  const {
    data: messagingProfiles = [],
    isLoading: profilesLoading,
    error: messagingProfileError,
  } = useQuery<TelnyxMessagingProfile[], RequestError>({
    queryKey: ['messaging_profiles'],
    queryFn: getMessagingProfiles,
    enabled: messagingProfileEnabled,
    retry: false,
  });

  const getWebhookURL = () => {
    if (!entity?.webhook_id) return undefined;
    if (LOCAL) return `https://noc-telnyx-eu.ngrok.io/event/${entity?.webhook_id}`;

    const isInternalProd = INTERNAL_CLIENT && window.location.origin.includes('prod');
    const isInternalDev = INTERNAL_CLIENT && !isInternalProd;

    if (window.location.origin.includes('dev') || isInternalDev)
      return `https://apidev.telnyx.com/v2/flow/event/${entity?.webhook_id}`;

    return `https://api.telnyx.com/v2/flow/event/${entity?.webhook_id}`;
  };

  const isExecutionDisabled = (): boolean => {
    const webhookUrl = getWebhookURL();
    const entityAssigned = entity?.duplicate;
    const isEntityValid = !entityLoading && !entityAssigned;

    if (type === WorkflowExecutionType.InboundCall) {
      if (entityError || appsLoading || voiceAppError) return true;

      const selectedVoiceApp = applications.find((app) => app.id === profile_id);
      const webhookConfigured = Boolean(selectedVoiceApp?.webhook_event_url);
      const flowWebhookConfigured = selectedVoiceApp?.webhook_event_url === webhookUrl && isEntityValid;
      const configuredIncorrectly = webhookConfigured && !flowWebhookConfigured && isEntityValid;

      return Boolean(!rtc?.destinationNumber || !profile_id || configuredIncorrectly || entityAssigned);
    }

    if (type === WorkflowExecutionType.InboundMessage) {
      if (entityError || profilesLoading || messagingProfileError) return true;

      const selectedMessagingProfile = messagingProfiles.find((app) => app.id === profile_id);
      const webhookConfigured = Boolean(selectedMessagingProfile?.webhook_url);
      const flowWebhookConfigured = selectedMessagingProfile?.webhook_url === webhookUrl && isEntityValid;
      const configuredIncorrectly = webhookConfigured && !flowWebhookConfigured && isEntityValid;
      return Boolean(!profile_id || configuredIncorrectly || entityAssigned);
    }

    return false;
  };

  return { isExecutionDisabled, getWebhookURL, entity };
};
